import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Quote, Send, CheckCircle2, Clock, XCircle, MessageSquareText } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";
const MIN_LEN = 20;
const MAX_LEN = 400;
const MAX_ROLE = 80;

const STATUS_META = {
  pending: {
    Icon: Clock,
    label: "Awaiting admin review",
    tint: "bg-amber-50 border-amber-200 text-amber-800",
    dot: "#F59E0B",
  },
  approved: {
    Icon: CheckCircle2,
    label: "Approved — live on the landing page",
    tint: "bg-teal-50 border-teal-200 text-teal-800",
    dot: "#0D9488",
  },
  rejected: {
    Icon: XCircle,
    label: "Not accepted",
    tint: "bg-rose-50 border-rose-200 text-rose-700",
    dot: "#F43F5E",
  },
};

/**
 * User-facing form on the Profile page. Lets a happy user submit one testimonial
 * at a time; blocked while one is pending. Shows the status of their last few
 * submissions.
 */
export default function SubmitTestimonialCard() {
  const [quote, setQuote] = useState("");
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonials/mine");
      setMine(Array.isArray(data) ? data : []);
    } catch {
      /* silent — not critical */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingRow = mine.find((m) => m.status === "pending");
  const canSubmit = !pendingRow && quote.trim().length >= MIN_LEN && quote.length <= MAX_LEN;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/testimonials/submit", { quote: quote.trim(), role: role.trim() });
      toast.success("Thanks — your testimonial is in the review queue.");
      setQuote("");
      setRole("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const remainingChars = MAX_LEN - quote.length;

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-6"
      data-testid="submit-testimonial-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareText className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>
          Share Your Story
        </div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Submit a Testimonial</h3>
      <p className="text-sm text-slate-500 mb-5 leading-relaxed">
        Loving how peptides + nutrition tracking work together for you? Send us a short quote — if it's a fit,
        an admin will feature it on the landing page.
      </p>

      {/* Existing submissions summary */}
      {!loading && mine.length > 0 && (
        <div className="mb-5 space-y-2" data-testid="submit-testimonial-mine">
          {mine.slice(0, 3).map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = meta.Icon;
            return (
              <div
                key={r.id}
                className={`border rounded-lg p-3 flex items-start gap-3 ${meta.tint}`}
                data-testid={`submit-testimonial-mine-${r.status}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold">{meta.label}</div>
                  <div className="text-[13px] mt-1 text-slate-800 line-clamp-2 leading-snug">
                    {r.quote}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form — hidden while a submission is pending */}
      {!pendingRow ? (
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                Your quote
              </span>
              <span
                className={`text-[10px] tabular-nums ${
                  quote.length > MAX_LEN
                    ? "text-rose-600 font-semibold"
                    : quote.length >= MIN_LEN
                    ? "text-teal-600"
                    : "text-slate-400"
                }`}
              >
                {quote.length < MIN_LEN
                  ? `${MIN_LEN - quote.length} more chars`
                  : `${remainingChars} left`}
              </span>
            </div>
            <Textarea
              rows={4}
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              maxLength={MAX_LEN + 100}
              placeholder="What clicked once you started tracking peptides + nutrition together? Concrete detail beats hype."
              className="bg-white border-slate-200 text-slate-900 resize-y focus-visible:ring-0 focus-visible:border-[#0D9488]"
              data-testid="submit-testimonial-quote"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
              Your protocol (optional)
            </span>
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value.slice(0, MAX_ROLE))}
              maxLength={MAX_ROLE}
              placeholder="e.g. Retatrutide protocol · 14-week cut"
              className="mt-1 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
              data-testid="submit-testimonial-role"
            />
          </label>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
            <p className="text-[11px] text-slate-400 max-w-xs leading-relaxed">
              <Quote className="w-3 h-3 inline mr-1" strokeWidth={2} />
              We'll show your handle + initials on the landing carousel. Your email is never displayed.
            </p>
            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="text-white"
              style={{ background: canSubmit && !submitting ? TEAL : "#94A3B8" }}
              onMouseEnter={(e) => { if (canSubmit && !submitting) e.currentTarget.style.background = TEAL_D; }}
              onMouseLeave={(e) => { if (canSubmit && !submitting) e.currentTarget.style.background = TEAL; }}
              data-testid="submit-testimonial-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {submitting ? "Sending…" : "Submit for review"}
            </Button>
          </div>
        </form>
      ) : (
        <div
          className="rounded-lg border border-slate-200 bg-slate-50/40 p-4 text-center text-sm text-slate-600"
          data-testid="submit-testimonial-blocked"
        >
          You've got one in the queue already. As soon as an admin reviews it, you'll be able to submit
          another.
        </div>
      )}
    </div>
  );
}
